import { useEffect, useState } from "react";
import { Box, Grid, Typography, TextField, Button } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { formFieldStyles, labelStyles } from "../../styles/formStyles";
import { useAuth } from "../../auth/AuthProvider";
import { API_USER_SERVICE } from "../../config/Constants";

interface BuildingMappingProps {
  onNext: () => void;
  onBack: () => void;
}

const BuildingMapping = ({ onNext, onBack }: BuildingMappingProps) => {
  const [formData, setFormData] = useState({
    name: "",
    floorCount: "",
    zip_code: "",
    country: "",
    state: "",
    city: "",
  });

  const [errors, setErrors] = useState({
    name: "",
    floorCount: "",
    zip_code: "",
    country: "",
    state: "",
    city: "",
  });

  const [data, setData] = useState<any>(null);
  const [warehouse, setWarehouse] = useState<any>(null);
  const { getLoginUserInfo } = useAuth();

  const loggedInUser = getLoginUserInfo();

  useEffect(() => {
    const fetchWarehouse = async () => {
      try {
        const res = await fetch("http://localhost:8080/api/warehouse", {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });

        if (!res.ok) return;

        const data = await res.json();
        setWarehouse(data);
      } catch (err) {
        console.error("Warehouse fetch failed:", err);
      }
    };

    fetchWarehouse();
  }, []);

  useEffect(() => {
    const fetchBuilding = async () => {
      try {
        const res = await fetch("http://localhost:8080/api/buildings", {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
        if (!res.ok) return;
        const result = await res.json();
        setData(result);
        if (result && result.length > 0) {
          setFormData({
            name: result[0].name || "",
            floorCount: result[0].floors || "",
            zip_code: result[0].zip_code || "",
            country: result[0].country || "",
            state: result[0].state || "",
            city: result[0].city || "",
          });
        }
      } catch (err) {
        console.error("Prefill failed:", err);
      }
    };
    fetchBuilding();
  }, []);

  const validate = (): boolean => {
    const newErrors = {
      name: "",
      floorCount: "",
      zip_code: "",
      country: "",
      state: "",
      city: "",
    };
    let valid = true;

    if (!formData.name) {
      newErrors.name = "Building name is required";
      valid = false;
    }

    if (!formData.floorCount) {
      newErrors.floorCount = "Floor count is required";
      valid = false;
    } else if (isNaN(Number(formData.floorCount)) || Number(formData.floorCount) < 1) {
      newErrors.floorCount = "Enter a valid number";
      valid = false;
    }

    if (!formData.zip_code) {
      newErrors.zip_code = "Zip code is required";
      valid = false;
    }

    if (!formData.country) {
      newErrors.country = "Country is required";
      valid = false;
    }

    if (!formData.state) {
      newErrors.state = "State is required";
      valid = false;
    }

    if (!formData.city) {
      newErrors.city = "City is required";
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    try {
      if (!data?.length && loggedInUser?.workspaceId) {
        const payload = {
          companyId: loggedInUser?.companyId,
          warehouseId: loggedInUser?.workspaceId,
          name: formData.name,
          floorsCount: Number(formData.floorCount),
          addressLine1: formData.city,
          postalCode: formData.zip_code,
          country: formData.country,
          state: formData.state,
          city: formData.city
        };

        const res = await fetch(`${API_USER_SERVICE}/building`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${sessionStorage.getItem("token")}`,
          },
          body: JSON.stringify(payload),
        });

        if (!res.ok) throw new Error("Save failed");
        await res.json();
      }
      console.log("Building details saved before onNext:");
      onNext();
      console.log("Building details saved after onNext:");
    } catch (err) {
      console.error(err);
      alert("Failed to save building details");
    }
  };

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#CBD5E1",
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: "#94A3B8",
          },
        }}
      >
        <Typography variant="h4" fontWeight={600} mb={1}>
          Let's map your first building
        </Typography>

        <Typography variant="body2" color="text.secondary" mb={4}>
          We'll create a starter structure for you. Don't worry, you can change this later.
        </Typography>

        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography component="label" sx={labelStyles}>
                What do you call this building?
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="eg. Prowess building"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                error={!!errors.name}
                helperText={errors.name}
                sx={formFieldStyles}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography component="label" sx={labelStyles}>
                How many floors it have?
              </Typography>
              <TextField
                fullWidth
                size="small"
                type="number"
                placeholder="eg. 8"
                value={formData.floorCount}
                onChange={(e) => setFormData({ ...formData, floorCount: e.target.value })}
                error={!!errors.floorCount}
                helperText={errors.floorCount}
                sx={formFieldStyles}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography component="label" sx={labelStyles}>
                Zip / Postal Code <span style={{ color: "#d32f2f" }}>*</span>
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="eg. 531002"
                value={formData.zip_code}
                onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                error={!!errors.zip_code}
                helperText={errors.zip_code}
                sx={formFieldStyles}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography component="label" sx={labelStyles}>
                Country <span style={{ color: "#d32f2f" }}>*</span>
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="eg. India"
                value={formData.country}
                onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                error={!!errors.country}
                helperText={errors.country}
                sx={formFieldStyles}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography component="label" sx={labelStyles}>
                State / Province <span style={{ color: "#d32f2f" }}>*</span>
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="eg. Andhra Pradesh"
                value={formData.state}
                onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                error={!!errors.state}
                helperText={errors.state}
                sx={formFieldStyles}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography component="label" sx={labelStyles}>
                City <span style={{ color: "#d32f2f" }}>*</span>
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="eg. Visakhapatnam"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                error={!!errors.city}
                helperText={errors.city}
                sx={formFieldStyles}
              />
            </Grid>
          </Grid>
        </Box>
      </Box>

      <Box
        sx={{
          py: 2,
          px: { xs: 2, md: 3.5 },
          backgroundColor: "background.paper",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Button variant="text" sx={{ textTransform: "none", color: "#9AA0AF" }} onClick={onNext}>
          Skip
        </Button>

        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            onClick={onBack}
            sx={{
              borderRadius: "6px",
              textTransform: "none",
              color: "#000",
              border: "1px solid #000",
            }}
          >
            Back
          </Button>

          <Button
            variant="contained"
            onClick={handleSubmit}
            endIcon={<ArrowForwardIcon />}
            sx={{
              borderRadius: "6px",
              background: "#005EFF",
              color: "#fff",
              textTransform: "none",
            }}
          >
            Create starter layout
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
export default BuildingMapping;
